import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { GetStartedCTA } from "../components/DemoDataControls";
import { getSalesOrders, getSalesOrder, createSalesOrder, updateSalesOrder, deleteSalesOrder, SO_STATUSES } from "../lib/salesOrders";
import { createFromSalesOrder, countInvoicesForSalesOrder } from "../lib/invoices";
import { listCustomers } from "../lib/customers";
import { getProducts } from "../lib/products";
import { getStockMap } from "../lib/inventory";
import SalesOrderForm from "./SalesOrderForm";
import SalesOrderView, { SOStatusBadge } from "./SalesOrderView";

const PAGE_SIZE = 25;
const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;

function Th({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  return (
    <th style={S.th} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button onClick={() => onSort(col)} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}<span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

export default function SalesOrderManager({ autoNew, onAutoNew, openId, onOpenHandled, onOpenInvoice }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [completedSO, setCompletedSO] = useState(null); // { id, number } → "create invoice now?" prompt
  const [invoicing, setInvoicing] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [customerId, setCustomerId] = useState("all");
  const [sortBy, setSortBy] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const cur = "€";

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data, count: total } = await getSalesOrders({ search, status, customerId, page, pageSize: PAGE_SIZE, sortBy, sortDir }); setRows(data); setCount(total); }
    catch (e) { setError(e.message || "Could not load sales orders."); }
    finally { setLoading(false); }
  }, [search, status, customerId, sortBy, sortDir, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const loadRefs = useCallback(async () => {
    try { setCustomers(await listCustomers({ status: "active" })); } catch { /* filter degrades */ }
    try { const { data } = await getProducts({ pageSize: 1000, sortBy: "name" }); setProducts(data); } catch { /* picker degrades */ }
    try { setStockMap(await getStockMap()); } catch { /* availability degrades */ }
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  useEffect(() => {
    if (autoNew) { setEditing({}); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew]);

  useEffect(() => {
    if (!openId) return;
    (async () => { try { setViewing(await getSalesOrder(openId)); } catch (e) { setError(e.message); } finally { onOpenHandled && onOpenHandled(); } })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  const onSearch = (v) => { setSearch(v); setPage(1); };
  const onStatus = (v) => { setStatus(v); setPage(1); };
  const onCustomer = (v) => { setCustomerId(v); setPage(1); };
  const onSort = (col) => { setPage(1); if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(col); setSortDir("asc"); } };
  const clearFilters = () => { setSearch(""); setStatus("all"); setCustomerId("all"); setPage(1); };

  const openNew = () => setEditing({});
  const openEdit = async (o) => { try { setEditing(await getSalesOrder(o.id)); } catch (e) { setError(e.message); } };
  const openView = async (o) => { try { setViewing(await getSalesOrder(o.id)); } catch (e) { setError(e.message); } };

  const save = async (payload) => {
    const wasFulfilled = editing?.status === "fulfilled";
    const savedId = editing && editing.id ? (await updateSalesOrder(editing.id, payload), editing.id) : await createSalesOrder(payload);
    setEditing(null);
    await Promise.all([load(), loadRefs()]); // refresh list + stock (reservations changed)
    // D9 — when an order becomes complete, OFFER (don't force) creating the invoice.
    if (onOpenInvoice && payload.status === "fulfilled" && !wasFulfilled) {
      let number = payload.so_number || null;
      try { number = (await getSalesOrder(savedId))?.so_number || number; } catch { /* banner still works without the number */ }
      setCompletedSO({ id: savedId, number });
    }
  };

  // Create an invoice from a Sales Order and cross-navigate into its pre-filled editor.
  // Shared by the completion prompt and the "Create Invoice →" button in the view.
  const createInvoice = async (soId, soNumber) => {
    setInvoicing(true); setError("");
    try {
      const existing = await countInvoicesForSalesOrder(soId);
      if (existing > 0 && !window.confirm(`${soNumber || "This order"} already has ${existing} invoice${existing === 1 ? "" : "s"}. Create another?`)) return;
      const invId = await createFromSalesOrder(soId);
      setCompletedSO(null); setViewing(null);
      onOpenInvoice(invId);
    } catch (e) { setError(e.message); }
    finally { setInvoicing(false); }
  };
  const del = async (o) => {
    if (!window.confirm(`Delete ${o.so_number}? Its stock reservations will be released.`)) return;
    try { await deleteSalesOrder(o.id); await Promise.all([load(), loadRefs()]); } catch (e) { setError(e.message); }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const hasFilters = !!search || status !== "all" || customerId !== "all";
  const selStyle = { ...S.input, width: "auto", minWidth: 150 };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search SO no., customer, reference…" value={search} onChange={(e) => onSearch(e.target.value)} aria-label="Search sales orders" />
        <select style={selStyle} value={customerId} onChange={(e) => onCustomer(e.target.value)} aria-label="Filter by customer">
          <option value="all">All customers</option>
          {customers.map((cu) => <option key={cu.id} value={cu.id}>{cu.company_name}</option>)}
        </select>
        <select style={selStyle} value={status} onChange={(e) => onStatus(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          {SO_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{count} order{count === 1 ? "" : "s"}</span>
        <button style={S.btn()} onClick={openNew}>+ New Sales Order</button>
      </div>

      {completedSO && onOpenInvoice && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, background: C.surface2, border: `1px solid ${C.success}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, flexWrap: "wrap" }}>
          <span style={{ color: C.success, fontWeight: 700 }}>✅ {completedSO.number || "Sales order"} complete — create the invoice now?</span>
          <button style={{ ...S.btn(), opacity: invoicing ? 0.6 : 1 }} disabled={invoicing} onClick={() => createInvoice(completedSO.id, completedSO.number)}>{invoicing ? "Creating…" : "Create Invoice →"}</button>
          <button style={S.btn("ghost")} onClick={() => setCompletedSO(null)}>Not now</button>
        </div>
      )}

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={load}>Retry</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <Th label="SO Number" col="number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th}>Customer</th>
              <Th label="Order Date" col="order_date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Total" col="total" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th} aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={S.empty}>Loading…</td></tr>
            ) : count === 0 ? (
              <tr><td colSpan={6} style={S.empty}>
                {hasFilters ? (
                  <div>No sales orders match your filters.<div style={{ marginTop: 10 }}><button style={S.btn("secondary")} onClick={clearFilters}>Clear filters</button></div></div>
                ) : (
                  <GetStartedCTA onLoaded={load} fallback={
                    <div>
                      <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No sales orders yet</div>
                      <div style={{ marginBottom: 12 }}>Create a sales order to reserve stock against inventory.</div>
                      <button style={S.btn()} onClick={openNew}>+ New Sales Order</button>
                    </div>
                  } />
                )}
              </td></tr>
            ) : rows.map((o) => (
              <tr key={o.id} style={{ cursor: "pointer" }} onClick={() => openView(o)}>
                <td style={S.td}><span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 12 }}>{o.so_number}</span></td>
                <td style={S.td}>{o.customer_name || o.customers?.company_name || <span style={{ color: C.textDim }}>—</span>}</td>
                <td style={S.td}>{o.order_date || "—"}</td>
                <td style={S.td}><SOStatusBadge status={o.status} /></td>
                <td style={S.td}>{money(o.total_amount, cur)}</td>
                <td style={S.td} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button style={S.btn("ghost")} title="View" aria-label={`View ${o.so_number}`} onClick={() => openView(o)}>👁</button>
                    <button style={S.btn("ghost")} title="Edit" aria-label={`Edit ${o.so_number}`} onClick={() => openEdit(o)}>✎</button>
                    <button style={S.btn("ghost")} title="Delete" aria-label={`Delete ${o.so_number}`} onClick={() => del(o)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {count > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
            <span style={{ color: C.textMuted, fontSize: 12 }}>Showing {from}–{to} of {count}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button style={{ ...S.btn("secondary"), opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">← Prev</button>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Page {page} of {totalPages}</span>
              <button style={{ ...S.btn("secondary"), opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">Next →</button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <SalesOrderForm initial={editing} customers={customers} products={products} stockMap={stockMap} onCancel={() => setEditing(null)} onSave={save} />
      )}
      {viewing && (
        <SalesOrderView order={viewing} busy={invoicing} onClose={() => setViewing(null)} onEdit={(o) => { setViewing(null); openEdit(o); }}
          onCreateInvoice={onOpenInvoice ? (o) => createInvoice(o.id, o.so_number) : undefined} />
      )}
    </div>
  );
}
